/* eslint-disable react/jsx-max-props-per-line */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Chip,
  Dialog,
  IconButton,
  Link,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import FitScreenIcon from '@mui/icons-material/FitScreen';
import RotateLeftIcon from '@mui/icons-material/RotateLeft';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import FlipIcon from '@mui/icons-material/Flip';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import {
  formatArquivoPrecisaoMetros,
  formatEvidenciaGpsCaption,
  geoPointFromArquivoAdditions,
  getArquivoEnderecoLabel,
  isArquivoCarimbada,
  mapsUrlFromArquivoAdditions,
  parseArquivoAdditions,
} from '../../../helpers/arquivoAdditions';
import fileDownload from 'js-file-download';
import GeoPointMap from '../GeoPointMap';
import ArquivoGeoMapDialogV2 from '../filePreview/ArquivoGeoMapDialogV2';

const MIN_SCALE = 0.25;
const MAX_SCALE = 5;
const ZOOM_STEP = 0.25;
const FALLBACK_SRC = '/images/noImage.jpeg';
const INFO_PANEL_WIDTH = 300;
const DIALOG_WITH_PANEL_WIDTH = 1040;
const DIALOG_PLAIN_WIDTH = 720;
const DIALOG_PORTRAIT_HEIGHT = 820;
const DIALOG_LANDSCAPE_HEIGHT = 560;

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const initialTransform = () => ({
  scale: 1,
  rotate: 0,
  flipH: false,
  flipV: false,
  x: 0,
  y: 0,
});

const resolveSrc = (image) => {
  if (!image) return FALLBACK_SRC;
  if (image.src) return image.src;
  return FALLBACK_SRC;
};

const resolveTitle = (image) => {
  if (!image) return 'Imagem';
  if (image.nome) return image.nome;
  if (image.alt && !String(image.alt).includes('; ')) return image.alt;
  return 'Imagem';
};

const resolveDownloadFilename = (image) => {
  const nome = image?.nome || 'imagem';
  const extensao = image?.extensao ? String(image.extensao).replace(/^\./, '') : null;
  if (extensao) return `${nome}.${extensao}`;
  const src = image?.src || '';
  if (src.startsWith('data:image/')) {
    const mime = src.slice(5, src.indexOf(';'));
    const ext = mime.split('/')[1] || 'jpg';
    return `${nome}.${ext}`;
  }
  const fromUrl = src.split('?')[0].split('/').pop();
  if (fromUrl && fromUrl.includes('.')) return fromUrl;
  return `${nome}.jpg`;
};

const canDownloadImage = (image, src, broken) => {
  if (broken || !image) return false;
  if (image.file) return true;
  return Boolean(src && src !== FALLBACK_SRC);
};

const buildInfoRows = (image) => {
  const rows = [];

  if (image?.description) {
    rows.push({ label: 'Descrição', value: image.description });
  }

  return rows;
};

const ImageLinks = ({ links, onDark }) => {
  if (!links?.length) return null;
  return (
    <Stack direction="row" spacing={1.5} justifyContent="center" flexWrap="wrap" useFlexGap sx={{ pointerEvents: 'auto' }}>
      {links.map((link) => (
        <Link
          key={`${link.label}-${link.href}`}
          href={link.href}
          target="_blank"
          rel="noopener noreferrer"
          variant="caption"
          sx={{
            fontWeight: 600,
            color: onDark ? 'primary.light' : 'primary.main',
          }}
        >
          {link.label}
        </Link>
      ))}
    </Stack>
  );
};

ImageLinks.propTypes = {
  links: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    href: PropTypes.string,
  })),
  onDark: PropTypes.bool,
};

const ToolButton = ({ title, onClick, disabled, children }) => (
  <Tooltip title={title}>
    <span>
      <IconButton
        onClick={onClick}
        disabled={disabled}
        size="small"
        sx={{
          color: 'common.white',
          bgcolor: (theme) => alpha(theme.palette.common.white, 0.12),
          '&:hover': {
            bgcolor: (theme) => alpha(theme.palette.common.white, 0.22),
          },
          '&.Mui-disabled': {
            color: (theme) => alpha(theme.palette.common.white, 0.28),
          },
        }}
      >
        {children}
      </IconButton>
    </span>
  </Tooltip>
);

ToolButton.propTypes = {
  title: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  children: PropTypes.node.isRequired,
};

const InfoRow = ({ label, value }) => (
  <Stack spacing={0.25}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
      {value}
    </Typography>
  </Stack>
);

InfoRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
};

const ViewerToolbar = ({
  zoomBy,
  resetTransform,
  setTransform,
}) => (
  <Stack
    direction="row"
    spacing={0.75}
    sx={{
      pointerEvents: 'auto',
      px: 1,
      py: 0.75,
      borderRadius: 8,
      bgcolor: (theme) => alpha(theme.palette.common.black, 0.55),
    }}
  >
    <ToolButton title="Diminuir zoom" onClick={() => zoomBy(-ZOOM_STEP)}>
      <ZoomOutIcon fontSize="small" />
    </ToolButton>
    <ToolButton title="Aumentar zoom" onClick={() => zoomBy(ZOOM_STEP)}>
      <ZoomInIcon fontSize="small" />
    </ToolButton>
    <ToolButton title="Ajustar à tela" onClick={resetTransform}>
      <FitScreenIcon fontSize="small" />
    </ToolButton>
    <ToolButton
      title="Girar à esquerda"
      onClick={() => setTransform((prev) => ({ ...prev, rotate: prev.rotate - 90 }))}
    >
      <RotateLeftIcon fontSize="small" />
    </ToolButton>
    <ToolButton
      title="Girar à direita"
      onClick={() => setTransform((prev) => ({ ...prev, rotate: prev.rotate + 90 }))}
    >
      <RotateRightIcon fontSize="small" />
    </ToolButton>
    <ToolButton
      title="Inverter horizontal"
      onClick={() => setTransform((prev) => ({ ...prev, flipH: !prev.flipH }))}
    >
      <FlipIcon fontSize="small" />
    </ToolButton>
    <ToolButton
      title="Inverter vertical"
      onClick={() => setTransform((prev) => ({ ...prev, flipV: !prev.flipV }))}
    >
      <FlipIcon fontSize="small" sx={{ transform: 'rotate(90deg)' }} />
    </ToolButton>
  </Stack>
);

ViewerToolbar.propTypes = {
  zoomBy: PropTypes.func.isRequired,
  resetTransform: PropTypes.func.isRequired,
  setTransform: PropTypes.func.isRequired,
};

const PhotoNavArrows = ({ canNav, goPrev, goNext }) => {
  if (!canNav) return null;

  const sideSx = {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    zIndex: 2,
    pointerEvents: 'auto',
  };

  return (
    <>
      <Box sx={{ ...sideSx, left: 8 }}>
        <ToolButton title="Anterior" onClick={goPrev}>
          <NavigateBeforeIcon fontSize="medium" />
        </ToolButton>
      </Box>
      <Box sx={{ ...sideSx, right: 8 }}>
        <ToolButton title="Próxima" onClick={goNext}>
          <NavigateNextIcon fontSize="medium" />
        </ToolButton>
      </Box>
    </>
  );
};

PhotoNavArrows.propTypes = {
  canNav: PropTypes.bool.isRequired,
  goPrev: PropTypes.func.isRequired,
  goNext: PropTypes.func.isRequired,
};

const ImageViewerV2 = ({
  open,
  onClose,
  images,
  activeIndex,
  showInfoPanel,
}) => {
  const theme = useTheme();
  const [index, setIndex] = useState(activeIndex || 0);
  const [transform, setTransform] = useState(initialTransform);
  const [broken, setBroken] = useState(false);
  const [imgSize, setImgSize] = useState({ w: 0, h: 0 });
  const [openGeoMap, setOpenGeoMap] = useState(false);
  const dragRef = useRef(null);

  const list = images || [];
  const safeIndex = list.length ? Math.min(Math.max(index, 0), list.length - 1) : 0;
  const current = list[safeIndex];
  const src = broken ? FALLBACK_SRC : resolveSrc(current);
  const title = resolveTitle(current);
  const extraAlt =
    current?.alt
    && current.alt !== title
    && !String(current.alt).includes('; ')
      ? current.alt
      : null;
  const carimbada = isArquivoCarimbada(current?.additions);
  const gpsCaption = carimbada ? null : formatEvidenciaGpsCaption(current?.additions);
  const canNav = list.length > 1;
  const infoRows = useMemo(() => buildInfoRows(current), [current]);
  const geoPoint = useMemo(
    () => geoPointFromArquivoAdditions(current?.additions, title),
    [current?.additions, title],
  );
  const mapsUrl = useMemo(
    () => mapsUrlFromArquivoAdditions(current?.additions),
    [current?.additions],
  );
  const locationEndereco = useMemo(
    () => getArquivoEnderecoLabel(current?.additions),
    [current?.additions],
  );
  const locationMeta = useMemo(() => {
    const data = parseArquivoAdditions(current?.additions);
    if (!data) return { coords: null, precisao: null };
    const coords = data.latitude != null && data.longitude != null
      ? `${data.latitude} / ${data.longitude}`
      : null;
    const precisao = formatArquivoPrecisaoMetros(data.precisao);
    return { coords, precisao };
  }, [current?.additions]);
  const showLocation = Boolean(geoPoint || locationEndereco);
  const canDownload = canDownloadImage(current, src, broken);

  const handleDownload = useCallback(async () => {
    if (!canDownload) return;
    const filename = resolveDownloadFilename(current);
    if (current?.file) {
      fileDownload(current.file, filename);
      return;
    }
    try {
      const response = await fetch(src);
      const blob = await response.blob();
      fileDownload(blob, filename);
    } catch {
      window.open(src, '_blank', 'noopener,noreferrer');
    }
  }, [canDownload, current, src]);

  const resetTransform = useCallback(() => {
    setTransform(initialTransform());
    dragRef.current = null;
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    setIndex(activeIndex || 0);
    resetTransform();
    setBroken(false);
    setImgSize({ w: 0, h: 0 });
    return undefined;
  }, [open, activeIndex, resetTransform]);

  useEffect(() => {
    setBroken(false);
    setImgSize({ w: 0, h: 0 });
    resetTransform();
    setOpenGeoMap(false);
  }, [safeIndex, resetTransform]);

  useEffect(() => {
    if (!open) setOpenGeoMap(false);
  }, [open]);

  const zoomBy = useCallback((delta) => {
    setTransform((prev) => ({
      ...prev,
      scale: clamp(Number((prev.scale + delta).toFixed(2)), MIN_SCALE, MAX_SCALE),
    }));
  }, []);

  const goPrev = useCallback(() => {
    if (!canNav) return;
    setIndex((prev) => (prev <= 0 ? list.length - 1 : prev - 1));
  }, [canNav, list.length]);

  const goNext = useCallback(() => {
    if (!canNav) return;
    setIndex((prev) => (prev >= list.length - 1 ? 0 : prev + 1));
  }, [canNav, list.length]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') {
        if (openGeoMap) {
          setOpenGeoMap(false);
          return;
        }
        onClose();
        return;
      }
      if (openGeoMap) return;
      if (event.key === 'ArrowLeft') goPrev();
      if (event.key === 'ArrowRight') goNext();
      if (event.key === '+' || event.key === '=') zoomBy(ZOOM_STEP);
      if (event.key === '-' || event.key === '_') zoomBy(-ZOOM_STEP);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, openGeoMap, onClose, goPrev, goNext, zoomBy]);

  useEffect(() => {
    if (!open) return undefined;
    const onWheel = (event) => {
      if (openGeoMap) return;
      if (event.target?.closest?.('.leaflet-container')) return;
      event.preventDefault();
      zoomBy(event.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP);
    };
    document.addEventListener('wheel', onWheel, { passive: false, capture: true });
    return () => document.removeEventListener('wheel', onWheel, { capture: true });
  }, [open, openGeoMap, zoomBy]);

  const onPointerDown = (event) => {
    if (transform.scale <= 1) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    dragRef.current = {
      x: event.clientX,
      y: event.clientY,
      ox: transform.x,
      oy: transform.y,
    };
  };

  const onPointerMove = (event) => {
    if (!dragRef.current) return;
    const drag = dragRef.current;
    setTransform((prev) => ({
      ...prev,
      x: drag.ox + (event.clientX - drag.x),
      y: drag.oy + (event.clientY - drag.y),
    }));
  };

  const onPointerUp = () => {
    dragRef.current = null;
  };

  const scaleX = transform.scale * (transform.flipH ? -1 : 1);
  const scaleY = transform.scale * (transform.flipV ? -1 : 1);
  const imgTransform = `translate(${transform.x}px, ${transform.y}px) rotate(${transform.rotate}deg) scale(${scaleX}, ${scaleY})`;

  const toolbarProps = {
    zoomBy,
    resetTransform,
    setTransform,
  };

  const panelBg = theme.palette.mode === 'dark'
    ? alpha(theme.palette.neutral[900], 0.96)
    : theme.palette.neutral[100];

  const isLandscape = imgSize.w > imgSize.h && imgSize.w > 0;
  const dialogWidth = showInfoPanel
    ? DIALOG_WITH_PANEL_WIDTH
    : (isLandscape ? 960 : DIALOG_PLAIN_WIDTH);
  const dialogHeight = isLandscape ? DIALOG_LANDSCAPE_HEIGHT : DIALOG_PORTRAIT_HEIGHT;
  const photoBg = theme.palette.common.black;

  const infoPanel = showInfoPanel ? (
    <Box
      sx={{
        width: { xs: '100%', md: INFO_PANEL_WIDTH },
        flexShrink: 0,
        bgcolor: panelBg,
        borderLeft: { md: `1px solid ${alpha(theme.palette.divider, 0.12)}` },
        borderTop: { xs: `1px solid ${alpha(theme.palette.divider, 0.12)}`, md: 'none' },
        overflowY: 'auto',
        maxHeight: { xs: '32vh', md: '100%' },
      }}
    >
      <Stack spacing={2} sx={{ p: 2.5 }}>
        <Stack spacing={1}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, wordBreak: 'break-word' }}>
            {title}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            {carimbada ? (
              <Chip size="small" label="Carimbada na foto" color="primary" variant="outlined" />
            ) : null}
            {canNav ? (
              <Typography variant="caption" color="text.secondary">
                {safeIndex + 1} / {list.length}
              </Typography>
            ) : null}
          </Stack>
          <ImageLinks links={current?.links} />
        </Stack>
        {canDownload ? (
          <Button
            size="small"
            variant="outlined"
            fullWidth
            startIcon={<CloudDownloadIcon />}
            onClick={handleDownload}
          >
            Baixar arquivo
          </Button>
        ) : null}
        {infoRows.map((row) => (
          <InfoRow key={row.label} label={row.label} value={row.value} />
        ))}
        {showLocation ? (
          <Stack spacing={1}>
            <Typography variant="caption" color="text.secondary">
              Localização
            </Typography>
            {geoPoint ? (
              <Box
                sx={(t) => ({
                  position: 'relative',
                  borderRadius: 0.5,
                  overflow: 'hidden',
                  border: `1px solid ${alpha(t.palette.divider, 0.8)}`,
                })}
              >
                <GeoPointMap
                  points={[geoPoint]}
                  active={open && showInfoPanel}
                  height={148}
                  scrollWheelZoom={false}
                  dragging={false}
                />
                <Tooltip title="Expandir mapa">
                  <IconButton
                    size="small"
                    onClick={() => setOpenGeoMap(true)}
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      zIndex: 500,
                      bgcolor: (t) => alpha(t.palette.common.white, 0.92),
                      boxShadow: 1,
                      '&:hover': {
                        bgcolor: 'common.white',
                      },
                    }}
                  >
                    <OpenInFullIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            ) : null}
            {locationEndereco ? (
              <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                {locationEndereco}
              </Typography>
            ) : null}
            {locationMeta.coords ? (
              <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                Lat/Long: {locationMeta.coords}
              </Typography>
            ) : null}
            {locationMeta.precisao ? (
              <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                Precisão: {locationMeta.precisao}
              </Typography>
            ) : null}
          </Stack>
        ) : null}
        {carimbada && infoRows.length === 0 && !showLocation ? (
          <Typography variant="body2" color="text.secondary">
            A localização já está registrada na imagem.
          </Typography>
        ) : null}
      </Stack>
    </Box>
  ) : null;

  const photoStage = (
    <Box
      sx={{
        flex: showInfoPanel ? 1 : undefined,
        height: showInfoPanel ? '100%' : '100%',
        width: '100%',
        minWidth: 0,
        minHeight: 0,
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        cursor: transform.scale > 1 ? 'grab' : 'default',
        userSelect: 'none',
        bgcolor: photoBg,
        pt: showInfoPanel ? 1 : 5,
        pb: showInfoPanel ? 7 : 12,
        boxSizing: 'border-box',
      }}
    >
      <Box
        component="img"
        alt={title}
        src={src}
        draggable={false}
        onError={() => setBroken(true)}
        onLoad={(event) => {
          setImgSize({
            w: event.currentTarget.naturalWidth,
            h: event.currentTarget.naturalHeight,
          });
        }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        sx={{
          maxWidth: '100%',
          maxHeight: '100%',
          objectFit: 'contain',
          transform: imgTransform,
          transformOrigin: 'center center',
          touchAction: 'none',
        }}
      />
      <PhotoNavArrows canNav={canNav} goPrev={goPrev} goNext={goNext} />
      <Stack
        spacing={1}
        alignItems="center"
        sx={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 16,
          px: 2,
          zIndex: 2,
          pointerEvents: 'none',
        }}
      >
        <ViewerToolbar {...toolbarProps} />
        {!showInfoPanel ? (
          <Stack spacing={0.25} alignItems="center" sx={{ maxWidth: '90%' }}>
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
              <Typography
                variant="body2"
                sx={{ color: 'common.white', fontWeight: 600, textAlign: 'center' }}
              >
                {title}
              </Typography>
              {carimbada ? (
                <Chip
                  size="small"
                  label="Carimbada na foto"
                  sx={{
                    height: 22,
                    color: 'common.white',
                    bgcolor: (t) => alpha(t.palette.common.white, 0.16),
                    '& .MuiChip-label': { px: 1, fontSize: 11 },
                  }}
                />
              ) : null}
              {canNav ? (
                <Typography
                  variant="caption"
                  sx={{ color: (t) => alpha(t.palette.common.white, 0.7) }}
                >
                  {safeIndex + 1} / {list.length}
                </Typography>
              ) : null}
            </Stack>
            <ImageLinks links={current?.links} onDark />
            {gpsCaption ? (
              <Typography
                variant="caption"
                sx={{
                  color: (t) => alpha(t.palette.common.white, 0.72),
                  textAlign: 'center',
                }}
              >
                {gpsCaption}
              </Typography>
            ) : extraAlt ? (
              <Typography
                variant="caption"
                sx={{
                  color: (t) => alpha(t.palette.common.white, 0.72),
                  textAlign: 'center',
                }}
              >
                {extraAlt}
              </Typography>
            ) : null}
          </Stack>
        ) : null}
      </Stack>
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={false}
      sx={{ zIndex: (t) => t.zIndex.modal + 4 }}
      slotProps={{
        paper: {
          sx: {
            width: `min(96vw, ${dialogWidth}px)`,
            maxWidth: '96vw',
            height: `min(92vh, ${dialogHeight}px)`,
            maxHeight: '92vh',
            bgcolor: showInfoPanel ? panelBg : photoBg,
            backgroundImage: 'none',
            overflow: 'hidden',
            borderRadius: 1,
            display: 'flex',
            flexDirection: 'column',
            transition: (t) => t.transitions.create('height', { duration: 200 }),
          },
        },
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 12,
          right: 12,
          zIndex: 3,
        }}
      >
        <ToolButton title="Fechar (Esc)" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </ToolButton>
      </Box>

      {showInfoPanel ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            flex: 1,
            minHeight: 0,
            width: '100%',
          }}
        >
          {photoStage}
          {infoPanel}
        </Box>
      ) : (
        <Box sx={{ flex: 1, minHeight: 0, position: 'relative' }}>
          {photoStage}
        </Box>
      )}
      <ArquivoGeoMapDialogV2
        open={openGeoMap}
        onClose={() => setOpenGeoMap(false)}
        point={geoPoint}
        title={title}
        mapsUrl={mapsUrl}
        coords={locationMeta.coords}
        precisao={locationMeta.precisao}
        zIndex={(t) => t.zIndex.modal + 8}
      />
    </Dialog>
  );
};

ImageViewerV2.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  images: PropTypes.arrayOf(
    PropTypes.shape({
      src: PropTypes.string,
      nome: PropTypes.string,
      alt: PropTypes.string,
      description: PropTypes.string,
      links: PropTypes.arrayOf(PropTypes.shape({
        label: PropTypes.string,
        href: PropTypes.string,
      })),
      extensao: PropTypes.string,
      file: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
      additions: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    }),
  ),
  activeIndex: PropTypes.number,
  showInfoPanel: PropTypes.bool,
};

ImageViewerV2.defaultProps = {
  images: [],
  activeIndex: 0,
  showInfoPanel: false,
};

export default ImageViewerV2;
